'use client';

import { useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Send, Loader2, MessageSquare, AlertCircle, ImagePlus, X, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { UserAreaShell } from '../../user-area-shell';

const TYPES = [
  { value: 1, label: '功能建议', desc: '想新增什么功能' },
  { value: 2, label: 'BUG反馈', desc: '发现了什么问题' },
  { value: 3, label: '使用问题', desc: '不会用某功能' },
  { value: 4, label: '投诉', desc: '对服务不满意' },
  { value: 5, label: '其他', desc: '其他想说的话' },
];

const MAX_IMAGES = 9;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export default function FeedbackNewClient({ inIframe = false }: { inIframe?: boolean } = {}) {
  const router = useRouter();
  const [type, setType] = useState(1);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState<Array<{ url: string; name: string; size: number }>>([]); // 上传后的图片数组
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (images.length + files.length > MAX_IMAGES) {
      toast.error(`最多上传 ${MAX_IMAGES} 张图片`);
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_IMAGE_SIZE) {
          toast.error(`${file.name} 超过 5MB`);
          continue;
        }
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} 不是图片`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const r = await fetch('/api.php?s=/api/Common/upload', {
          method: 'POST',
          body: fd,
          credentials: 'include',
        });
        const j = await r.json();
        if (j.code === 1 && j.data?.fullurl) {
          // P0 修复 (2026-07-15): 存 {url, name, size} 对象数组 (而不是只存 URL 字符串)
          //   PHP 端 Feedback::submit 现在兼容 2 种格式, 但 object[] 更规范
          //   - 保留 file.name 和 file.size, 用户上传时可见
          setImages(prev => [...prev, { url: j.data.fullurl, name: file.name, size: file.size }]);
        } else {
          toast.error(`${file.name} 上传失败：${j.msg || '未知错误'}`);
        }
      }
    } catch (err) {
      toast.error('图片上传失败');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (idx: number) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('请输入标题'); return; }
    if (!content.trim()) { toast.error('请输入内容'); return; }
    if (title.length > 200) { toast.error('标题不能超过 200 字'); return; }
    if (content.length > 5000) { toast.error('内容不能超过 5000 字'); return; }

    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.set('type', String(type));
      formData.set('title', title.trim());
      formData.set('content', content.trim());
      formData.set('contact', contact.trim());
      formData.set('images', JSON.stringify(images));

      const res = await fetch('/dongniai/api/feedback/submit/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      const json = await res.json();
      if (json.code === 1 || json.success) {
        toast.success('提交成功！我们会尽快处理');
        // P0 修复 (2026-07-15): Next.js proxyPost 用 apiSuccess 把 data flatten 了
        //   → 响应是 {success: true, id: '27'} 不是 {success: true, data: {id: '27'}}
        //   所以 json.data?.id 是 undefined, 之前会跳到 list 而非详情页
        const newId = json.id ?? json.data?.id;
        if (newId) {
          router.push(`/u/feedback/${newId}`);
        } else {
          router.push('/u/feedback/list');
        }
      } else if (json.msg && json.msg.includes('登录')) {
        toast.error('请先登录');
        // 2026-07-07 老板反馈: 不跳登录, 让 TopNav 始终可见 — 用户点顶栏 [登录] 按钮
        toast.info('请先登录 — 点右上角 [登录] 按钮');
      } else {
        toast.error(json.msg || '提交失败');
      }
    } catch (e) {
      toast.error('网络错误');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <UserAreaShell inIframe={inIframe}>
    <div className="min-h-screen bg-background">
      {/* 2026-07-14 P2 + 07-15 微调: 提交按钮在底部 sticky bar, 子顶栏只放「标题 + 我的反馈」入口
          (2026-07-15 老板: 去掉「← 返回」按钮 — 顶栏 TopNav 已经提供全站导航, 子顶栏的返回冗余) */}
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          {/* 2026-07-15 老板: 去掉「← 返回」按钮,只保留标题 + 「我的反馈」 */}
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare className="size-5 text-primary shrink-0" />
            <h1 className="text-lg font-semibold truncate">提交反馈</h1>
          </div>
          {/* 2026-07-15 老板: 加"我的反馈"按钮, 点击进反馈列表 */}
          <Button variant="outline" size="sm" onClick={() => router.push('/u/feedback/list')}>
            <List className="size-4 mr-1" />
            我的反馈
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* 类型选择 */}
        <Card className="p-4">
          <h2 className="text-sm font-medium mb-3">反馈类型</h2>
          <div className="grid grid-cols-2 gap-2">
            {TYPES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  type === t.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="font-medium text-sm">{t.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t.desc}</div>
              </button>
            ))}
          </div>
        </Card>

        {/* 标题 */}
        <Card className="p-4">
          <h2 className="text-sm font-medium mb-2">
            标题 <span className="text-destructive">*</span>
            <span className="text-xs text-muted-foreground ml-2">({title.length}/200)</span>
          </h2>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="一句话描述你的问题或建议"
            maxLength={200}
            className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </Card>

        {/* 内容 */}
        <Card className="p-4">
          <h2 className="text-sm font-medium mb-2">
            详细内容 <span className="text-destructive">*</span>
            <span className="text-xs text-muted-foreground ml-2">({content.length}/5000)</span>
          </h2>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="详细描述你遇到的问题或建议,我们会认真阅读每一条反馈"
            maxLength={5000}
            rows={8}
            className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
        </Card>

        {/* 图片上传（最多 9 张） */}
        <Card className="p-4">
          <h2 className="text-sm font-medium mb-2">
            截图（可选）
            <span className="text-xs text-muted-foreground ml-2">({images.length}/{MAX_IMAGES},单张 ≤ 5MB)</span>
          </h2>
          <div className="grid grid-cols-3 gap-2">
            {images.map(({ url, name }, idx) => (
              <div key={idx} className="relative aspect-square border rounded-md overflow-hidden bg-muted">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={url} alt={name || `截图${idx + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                  aria-label="删除"
                >
                  <X className="size-3" />
                </button>
              </div>
            ))}
            {images.length < MAX_IMAGES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="aspect-square border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <Loader2 className="size-6 animate-spin" />
                ) : (
                  <>
                    <ImagePlus className="size-6" />
                    <span className="text-xs mt-1">添加图片</span>
                  </>
                )}
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImagePick}
            className="hidden"
          />
        </Card>

        {/* 联系方式(可选) */}
        <Card className="p-4">
          <h2 className="text-sm font-medium mb-2">
            联系方式 <span className="text-xs text-muted-foreground">(可选,仅在需要详细沟通时填写)</span>
          </h2>
          <input
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="手机号/微信号/QQ/邮箱"
            maxLength={100}
            className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </Card>

        {/* 提示 + 提交按钮 (老板 2026-07-14 P3 反馈:
            把「提交反馈」按钮放在蓝色提示行的右侧, 不要 sticky 底部栏) */}
        <Card className="p-4 bg-blue-50/50 border-blue-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-2 text-sm text-blue-800 flex-1 min-w-0">
              <AlertCircle className="size-4 shrink-0 mt-0.5" />
              <p className="break-words">提交后管理员会在 1-2 个工作日内回复,你可以在"我的反馈"查看进度和回复。</p>
            </div>
            <Button onClick={handleSubmit} disabled={submitting || uploading} size="default" className="shrink-0 px-5">
              {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              <span className="ml-1">提交反馈</span>
            </Button>
          </div>
        </Card>
      </div>
    </div>
    </UserAreaShell>
  );
}
